/**
 * ImageReviewBlock — review only, Image and Comparaison rows (UI-SPEC §2.16, lot F step 14).
 * Two rows, an 84 px uppercase label column:
 *   CRITÈRES — the classified criteria on one trigger, which opens the searchable term Pick.
 *   CIBLE    — TargetChip (detected target, pick or remove one).
 * The server re-validates a picked target on save (lot F step 4).
 */
import { useState } from 'react';
import { Box } from '@mui/material';
import { Pick } from './Pick';
import { TargetChip } from './TargetChip';
import type { PickedTarget } from './TargetChip';

export interface ImageReviewBlockProps {
  brandId: string;
  criteriaOptions: string[];
  criteria?: string[];
  target: PickedTarget | null;
  targetLookup?: 'failed';
  onCriteriaChange: (criteria: string[]) => void;
  onTargetChange: (target: PickedTarget | null) => void;
  onRetryDetection?: () => void;
  disabled?: boolean;
}

const labelSx = {
  width: 84, flexShrink: 0, fontSize: 10.5, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '.04em',
} as const;

export function ImageReviewBlock({
  brandId,
  criteriaOptions,
  criteria = [],
  target,
  targetLookup,
  onCriteriaChange,
  onTargetChange,
  onRetryDetection,
  disabled,
}: ImageReviewBlockProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
      <CriteriaChips criteriaOptions={criteriaOptions} criteria={criteria} onCriteriaChange={onCriteriaChange} disabled={disabled} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {/* i18n: promptStudio.review.target */}
        <Box sx={labelSx}>Cible</Box>
        <TargetChip
          brandId={brandId}
          target={target}
          targetLookup={targetLookup}
          onChange={onTargetChange}
          onRetryDetection={onRetryDetection}
          disabled={disabled}
        />
      </Box>
    </Box>
  );
}

/** The criteria a pick leaves, in Brand Knowledge order (a term no longer there is dropped: the save would refuse it). */
export function pickedCriteria(options: string[], draft: string[]): string[] {
  return options.filter((c) => draft.includes(c));
}

/**
 * CRITÈRES — the current criteria on ONE trigger; a click opens the term Pick (search at the top,
 * multi). Ticks are a draft, handed to `onCriteriaChange` ONCE on close when they changed, so a
 * saved prompt gets one PATCH, never one per tick racing each other (owner, 2026-10-30: 100+ terms).
 */
export function CriteriaChips({
  criteriaOptions,
  criteria = [],
  onCriteriaChange,
  disabled,
}: {
  criteriaOptions: string[];
  criteria?: string[];
  onCriteriaChange: (criteria: string[]) => void;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState<string[] | null>(null);
  const [q, setQ] = useState('');
  const close = () => {
    if (draft) {
      const next = pickedCriteria(criteriaOptions, draft);
      if (next.join('\u0000') !== pickedCriteria(criteriaOptions, criteria).join('\u0000')) onCriteriaChange(next);
    }
    setDraft(null);
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {/* i18n: promptStudio.review.criteria */}
      <Box sx={labelSx}>Critères</Box>
      <TermTrigger
        text={criteria.length ? criteria.join(', ') : 'aucun' /* i18n: promptStudio.vocab.noneShort */}
        disabled={disabled}
        onClick={() => { setQ(''); setDraft(criteria); }}
      />
      <Pick
        open={draft !== null}
        kind="term"
        multi
        termOptions={criteriaOptions}
        searchPlaceholder={`Rechercher parmi ${criteriaOptions.length} critères`} // i18n: promptStudio.pick.searchTerms
        selected={draft ?? []}
        onToggle={(c) => setDraft((d) => (d ?? []).includes(c) ? (d ?? []).filter((x) => x !== c) : [...(d ?? []), c])}
        onClose={close}
        searchQuery={q}
        onSearchChange={setQ}
      />
    </Box>
  );
}

/**
 * TopicReviewBlock — Autorité and Recommandation rows, in the review and the opened prompt (owner,
 * 2026-10-30, beyond UI-SPEC §2.16): the theme / use case on ONE trigger; a click opens the term
 * Pick (search at the top, « Aucun » first); a pick closes it and is handed to `onTopicChange`.
 */
export function TopicReviewBlock({
  axis,
  topicOptions,
  topic,
  onTopicChange,
  disabled,
}: {
  axis: 'authority' | 'referral';
  topicOptions: string[];
  topic?: string;
  onTopicChange: (topic: string | undefined) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const noun = axis === 'authority' ? 'thèmes' : 'cas d’usage'; // i18n: promptStudio.pick.searchTerms
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
      {/* i18n: promptStudio.review.topic.authority / .referral */}
      <Box sx={labelSx}>{axis === 'authority' ? 'Thème' : 'Cas d’usage'}</Box>
      <TermTrigger
        text={topic ?? 'aucun' /* i18n: promptStudio.vocab.noneShort */}
        disabled={disabled}
        onClick={() => { setQ(''); setOpen(true); }}
      />
      <Pick
        open={open}
        kind="term"
        termOptions={topicOptions}
        searchPlaceholder={`Rechercher parmi ${topicOptions.length} ${noun}`}
        selected={topic ?? ''}
        onToggle={(v) => {
          setOpen(false);
          const next = v || undefined;
          if (next !== topic) onTopicChange(next);
        }}
        onClose={() => setOpen(false)}
        searchQuery={q}
        onSearchChange={setQ}
      />
    </Box>
  );
}

/** The current value(s) + ▾, one line whatever the vocabulary's size. */
function TermTrigger({ text, disabled, onClick }: { text: string; disabled?: boolean; onClick: () => void }) {
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={text}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: 360, height: 22, px: 0.875,
        borderRadius: '5px', fontSize: 11.5, color: 'text.primary', bgcolor: 'transparent',
        border: '1px solid', borderColor: 'divider', cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1, '&:hover': disabled ? {} : { bgcolor: 'rgba(0,0,0,.04)' },
      }}
    >
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</Box>
      <Box component="span" sx={{ color: 'text.disabled', fontSize: 10 }}>▾</Box>
    </Box>
  );
}
